import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, Path, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { fetchAndActivate, getValue } from "firebase/remote-config";
import { remoteConfig } from "../../../firebase/firebase";
import { RemoteConfig } from "../../../ampli/constants";
import { FileUploadManagerRef } from "../../../components/forms/fileUploadManager";
import { ROUTES } from "../../../setup/constants";
import { useKcToken } from "../../../setup/auth/useKcToken";
import { useOrganizationCode } from "../../../setup/util/extractOrganizationCode";
import { usePhoneRuleConfigs } from "../../../setup/util/usePhoneRuleConfigs";
import {
  buildFullPhoneNumber,
  isPhoneNumberValid,
  SA_FALLBACK_CONFIG,
  sanitizePhoneDigits,
  splitPhoneNumber,
} from "../../../setup/util/phoneRuleConfig.helpers";
import {
  getOrganization,
  getOrganizationPaymentMethods,
  getOrganizationTiers,
} from "../../../setup/api/services/organization";
import { IOrganizationFeeTemplate } from "../../../setup/api/services/organization/models";
import {
  EPaymentType,
  ESenderRole,
  IContractDetailsResponseData,
} from "../../../setup/api/services/contract/models";
import { IOrganizations } from "../../../setup/types/Organisation";
import { ECONTRACT_STATUS } from "../../../enums";
import { buildContractShareLink } from "../../../setup/util/contract";
import {
  isTwoPartyContract,
  isTwoPartyEditable,
} from "../../../setup/util/twoPartyContract";
import { getPartyValidationMessages } from "../create/partyPayload";
import {
  buildParties,
  buildPaymentMethods,
  IDraftIds,
  loadDraft,
  offeredBrands,
  saveDetails,
  saveEdit,
  saveParties,
  saveProgress,
  saveValueAndPublish,
  TOrganizationPaymentMethods,
  TwoPartySenderError,
} from "./api";
import { estimateFee } from "./fees";
import { parseSaudiPhone } from "./saudiPhone";
import { AuthConfig } from "../../../setup/context/config/authConfig";
import TwoPartyWizardView, { SUCCESS_STEP } from "./view";
import {
  buildSchema,
  DEFAULT_VALUES,
  STEP_FIELDS,
  TwoPartyContractInputs,
} from "./schema";

/**
 * Two-party transaction: the organization sells, adds one buyer, and Waffy holds the money until
 * the transaction completes. `/create-two-party-contract/:complexId?/:milestoneId?` — with both
 * ids it resumes a saved draft.
 */
const CreateTwoPartyContract = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { complexId: draftComplexId, milestoneId: draftMilestoneId } =
    useParams();
  const isResume = !!draftComplexId && !!draftMilestoneId;

  const organizationCode = useOrganizationCode(useKcToken());
  const { countries, isLoading: countriesLoading } = usePhoneRuleConfigs();

  const [organization, setOrganization] = useState<IOrganizations>();
  const [organizationMethods, setOrganizationMethods] =
    useState<TOrganizationPaymentMethods>({});
  const [feeTemplate, setFeeTemplate] = useState<IOrganizationFeeTemplate>();
  const [maxAmount, setMaxAmount] = useState(1000000);

  const [currentStep, setCurrentStep] = useState(0);
  const [ids, setIds] = useState<IDraftIds>({
    complexId: draftComplexId,
    milestoneId: draftMilestoneId,
  });
  const [draftLoading, setDraftLoading] = useState(isResume);
  // A published transaction the buyer has not paid yet is edited in place (WAF-3235): no
  // drafts are saved on the way, and the last step saves the edit instead of publishing.
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [published, setPublished] = useState<IContractDetailsResponseData>();
  const fileUploadRef = useRef<FileUploadManagerRef>(null);

  const findCountry = useCallback(
    (countryCode: string) =>
      countries.find((country) => country.countryCode === countryCode),
    [countries]
  );

  const isPhoneValid = useCallback(
    (countryCode: string, national: string) => {
      const country = findCountry(countryCode);
      if (!country) return /^\d{4,14}$/.test(sanitizePhoneDigits(national));
      return isPhoneNumberValid(national, country);
    },
    [findCountry]
  );

  // The seller's own mobiles: the signed-in user's, and the organization account's when it is a
  // phone number. The buyer may not be either (WAF-3235).
  const sellerPhones = useMemo(
    () =>
      [
        AuthConfig.getUserInfo()?.phoneNumber,
        organization?.accountUserIdentifier,
      ]
        .map((raw) => parseSaudiPhone(`${raw ?? ""}`).national)
        .filter((national) => isPhoneNumberValid(national, SA_FALLBACK_CONFIG))
        .map((national) => buildFullPhoneNumber(national, SA_FALLBACK_CONFIG)),
    [organization?.accountUserIdentifier]
  );

  const schema = useMemo(
    () =>
      buildSchema({
        t,
        isPhoneValid,
        maxAmount,
        sellerPhones,
        toE164: (national) =>
          buildFullPhoneNumber(national, SA_FALLBACK_CONFIG),
      }),
    [t, isPhoneValid, maxAmount, sellerPhones]
  );

  const methods = useForm<TwoPartyContractInputs>({
    mode: "onChange",
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(schema),
  });
  const {
    getValues,
    reset,
    trigger,
    watch,
    formState: { isDirty },
  } = methods;

  // ── Organization: the seller party, its payment methods and fee tiers ────
  useEffect(() => {
    if (!organizationCode) return;
    const code = organizationCode as string;
    getOrganization(code)
      .then((response) => setOrganization(response?.data))
      .catch(console.error);
    getOrganizationPaymentMethods(code)
      .then((response) =>
        setOrganizationMethods(response?.data?.paymentMethods || {})
      )
      .catch(console.error);
    getOrganizationTiers(code)
      .then((response) => setFeeTemplate(response?.data))
      .catch(console.error);
  }, [organizationCode]);

  // The same price ceiling the party modal enforces.
  useEffect(() => {
    fetchAndActivate(remoteConfig)
      .then(() => {
        const value = getValue(
          remoteConfig,
          "max_product_price"
        ) as unknown as RemoteConfig;
        if (+value?._value) setMaxAmount(+value._value);
      })
      .catch(console.error);
  }, []);

  // ── Resuming a saved draft ────────────────────────────────────────────────
  useEffect(() => {
    if (!isResume || countriesLoading) return;
    loadDraft(draftComplexId, draftMilestoneId)
      .then(({ complex, milestone }) => {
        const isDraft = milestone?.status === ECONTRACT_STATUS.DRAFT;
        if (
          !isTwoPartyContract(milestone) ||
          (!isDraft && !isTwoPartyEditable(milestone))
        ) {
          navigate(ROUTES.CONTRACT_DETAILS.replace(":id", draftMilestoneId), {
            replace: true,
          });
          return;
        }
        setEditMode(!isDraft);
        const buyer = milestone?.parties?.find(
          (party) => party?.role === ESenderRole.CUSTOMER
        );
        const { country, nationalNumber } = splitPhoneNumber(
          buyer?.phoneNumber,
          countries
        );
        const hasMethod = (type: EPaymentType) =>
          !!milestone?.paymentMethods?.[type]?.length;
        reset({
          title: complex?.itemDetail?.title || "",
          description: complex?.itemDetail?.description || "",
          files: [
            ...(milestone?.itemDetail?.images || []),
            ...(milestone?.itemDetail?.attachments || []),
          ],
          buyer: {
            isCompany: !!buyer?.companyUnifiedNumber,
            name: buyer?.sudoName || "",
            phoneCountryCode: country.countryCode,
            phoneNational: nationalNumber,
            companyName: buyer?.companyName || "",
            companyUnifiedNumber: buyer?.companyUnifiedNumber || "",
          },
          amount: milestone?.itemPrice || null,
          installments: {
            tabby: hasMethod(EPaymentType.TABBY),
            tamara: hasMethod(EPaymentType.TAMARA),
            neo: hasMethod(EPaymentType.NEO),
          },
        });
      })
      .catch(console.error)
      .finally(() => setDraftLoading(false));
    // countries are read once they have loaded; the draft is loaded once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResume, countriesLoading]);

  // ── Derived values ────────────────────────────────────────────────────────
  // Read field by field: the form mutates the nested `installments` object in place, so a memo
  // keyed on the object itself would miss a checkbox toggle.
  const [tabby, tamara, neo] = watch([
    "installments.tabby",
    "installments.tamara",
    "installments.neo",
  ]);
  const amount = watch("amount") || 0;

  const paymentMethods = useMemo(
    () => buildPaymentMethods(organizationMethods, { tabby, tamara, neo }),
    [organizationMethods, tabby, tamara, neo]
  );
  const estimate = useMemo(
    () => estimateFee(feeTemplate, amount, offeredBrands(paymentMethods)),
    [feeTemplate, amount, paymentMethods]
  );

  const buyerPhoneNumber = () => {
    const { phoneCountryCode, phoneNational } = getValues("buyer");
    const country = findCountry(phoneCountryCode);
    return country
      ? buildFullPhoneNumber(phoneNational, country)
      : `+${phoneCountryCode}${sanitizePhoneDigits(phoneNational)}`;
  };

  const parties = () =>
    organization
      ? buildParties(getValues(), organization, buyerPhoneNumber())
      : undefined;

  const validateStep = (step: number) =>
    trigger(STEP_FIELDS[step] as Path<TwoPartyContractInputs>[]);

  const showSaveError = (error: unknown) => {
    console.error(error);
    if (error instanceof TwoPartySenderError) {
      setSaveErrors([t("err_two_party_sender")]);
      return;
    }
    const messages = getPartyValidationMessages(error);
    const message = (error as { error?: { message?: string } })?.error?.message;
    setSaveErrors(
      messages.length ? messages : [message || t("lbl_something_went_wrong")]
    );
  };

  // ── Navigation ────────────────────────────────────────────────────────────
  const handleNext = async () => {
    if (!(await validateStep(currentStep))) return;
    if (fileUploadRef.current?.isUploading()) return;

    // Editing a published transaction: the steps only validate, the last one saves it all.
    if (editMode && currentStep < 2) {
      setSaveErrors([]);
      setCurrentStep((step) => step + 1);
      return;
    }

    setSaving(true);
    setSaveErrors([]);
    try {
      const values = getValues();
      if (editMode) {
        const buyerAndSeller = parties();
        if (!ids.complexId || !ids.milestoneId || !buyerAndSeller) return;
        await saveEdit(
          { complexId: ids.complexId, milestoneId: ids.milestoneId },
          values,
          paymentMethods,
          buyerAndSeller
        );
        reset(values);
        navigate(ROUTES.CONTRACT_DETAILS.replace(":id", ids.milestoneId), {
          replace: true,
        });
        return;
      }
      if (currentStep === 0) {
        setIds(await saveDetails(ids, values, paymentMethods, 1));
      } else if (currentStep === 1) {
        const buyerAndSeller = parties();
        if (!ids.complexId || !ids.milestoneId || !buyerAndSeller) return;
        await saveParties(
          { complexId: ids.complexId, milestoneId: ids.milestoneId },
          buyerAndSeller
        );
      } else {
        const buyerAndSeller = parties();
        if (!ids.complexId || !ids.milestoneId || !buyerAndSeller) return;
        setPublished(
          await saveValueAndPublish(
            { complexId: ids.complexId, milestoneId: ids.milestoneId },
            values,
            paymentMethods,
            buyerAndSeller,
            SUCCESS_STEP
          )
        );
        // The draft is now published — leaving no longer discards anything.
        reset(values);
      }
      setCurrentStep((step) => step + 1);
    } catch (error) {
      showSaveError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!(await validateStep(0))) {
      setCurrentStep(0);
      return;
    }
    setSaving(true);
    setSaveErrors([]);
    try {
      // The parties are saved only once the buyer step has been filled in validly.
      const withParties = currentStep >= 1 && (await validateStep(1));
      const saved = await saveProgress(
        ids,
        getValues(),
        paymentMethods,
        withParties ? parties() : undefined,
        currentStep
      );
      reset(getValues());
      navigate(ROUTES.CONTRACT_DETAILS.replace(":id", saved.complexId));
    } catch (error) {
      showSaveError(error);
    } finally {
      setSaving(false);
    }
  };

  const isSuccess = currentStep === SUCCESS_STEP;
  // WAF-3235: Next stays disabled on step one until there is a title.
  const titleFilled = !!watch("title")?.trim();
  const nextDisabled =
    (currentStep === 0 && !titleFilled) ||
    saving ||
    uploading ||
    (currentStep >= 1 && !organization) ||
    !organizationCode;

  return (
    <FormProvider {...methods}>
      <TwoPartyWizardView
        currentStep={currentStep}
        loading={draftLoading}
        saving={saving}
        saveErrors={saveErrors}
        nextDisabled={nextDisabled}
        organization={organization}
        organizationMethods={organizationMethods}
        feeTemplate={feeTemplate}
        estimate={estimate}
        fileUploadRef={fileUploadRef}
        onUploadStatusChange={setUploading}
        header={{
          isDirty: !isSuccess && (isDirty || (!isResume && !!ids.complexId)),
          // A draft started here is discarded on cancel; a resumed draft is left as it was.
          discardId: !isResume && !isSuccess ? ids.complexId : undefined,
          onLeave: () => (isSuccess ? navigate(ROUTES.HOME) : navigate(-1)),
        }}
        success={{
          buyerName: getValues("buyer.name"),
          shareLink: buildContractShareLink(published),
          amount,
          onViewDetails: () =>
            navigate(
              ROUTES.CONTRACT_DETAILS.replace(
                ":id",
                published?.id || ids.milestoneId || ""
              )
            ),
        }}
        onNext={handleNext}
        onPrev={
          currentStep > 0
            ? () => {
                setSaveErrors([]);
                setCurrentStep((step) => step - 1);
              }
            : undefined
        }
        onSaveDraft={editMode ? undefined : handleSaveDraft}
        editMode={editMode}
      />
    </FormProvider>
  );
};

export default CreateTwoPartyContract;
